import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { HelpCircle, X, Send, Bot, User, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { theme } from '../styles/theme';
import api from '../services/api';

// Mapa de rotas → título legível para o contexto da página
const PAGE_TITLES = {
  '/dashboard':          'Dashboard',
  '/agents-config':      'Configuração de Agentes',
  '/agents':             'Agentes',
  '/callcenter':         'Call Center',
  '/monitor':            'Monitor de Conversas',
  '/knowledge':          'Base de Conhecimento',
  '/team':               'Gestão de Equipe',
  '/billing':            'Plano e Uso',
  '/templates':          'Templates de Resposta',
  '/tasks':              'Tarefas',
  '/manager':            'Dashboard Gestores',
  '/onboarding':         'Guia de Configuração',
  '/users':              'Usuários',
  '/sallus/dashboard':   'Painel Admin',
  '/sallus/marketplace': 'Marketplace',
  '/sallus/analytics':   'Analytics',
  '/sallus/settings':    'Configurações da Plataforma',
  '/sallus/help':        'Manual de Help',
};

function getPageContext(pathname) {
  // Tenta match exato primeiro, depois por prefixo
  for (const [route, title] of Object.entries(PAGE_TITLES)) {
    if (pathname === route || pathname.startsWith(route + '/')) {
      return { path: pathname, title };
    }
  }
  return { path: pathname, title: 'Plataforma' };
}

const HelpChat = () => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const [open, setOpen]       = useState(false);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  // Initialize greeting after t is available
  useEffect(() => {
    setHistory([{ role: 'assistant', content: t('helpChat.greeting') }]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  const isPublic = ['/login', '/register'].includes(location.pathname);

  // Scroll automático ao receber nova mensagem
  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, open]);

  // Foca o input ao abrir
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open]);

  // Não exibe em páginas públicas
  if (!user || isPublic) return null;

  const pageContext = getPageContext(location.pathname);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;

    const userMsg = { role: 'user', content: msg };
    setHistory(h => [...h, userMsg]);
    setInput('');
    setLoading(true);

    // extrai tenantId da URL (ex: /agents-config/fgmax_ltda)
    const tenantId = location.pathname.split('/').find((seg, i, arr) =>
      i > 0 && arr[i - 1] !== 'sallus' && seg.length > 3 && !['dashboard','agents-config','agents','callcenter','monitor','knowledge','team','billing','templates','tasks','manager','users','campaigns','audit'].includes(seg)
    ) || user?.tenantId;

    try {
      const res = await api.post('/help/chat', {
        message: msg,
        pageContext,
        tenantId,
        history: history.slice(-8)
      });
      const { answer, actions = [] } = res.data;
      let content = answer;
      if (actions.length > 0) {
        const actionSummary = actions
          .filter(a => !a.result?.error)
          .map(a => {
            if (a.tool === 'install_agent')    return `✅ Agente instalado: **${a.result.instanceName || a.input.agentId}**`;
            if (a.tool === 'create_connector') return `✅ Conector criado: **${a.input.name}** (${a.input.type})`;
            if (a.tool === 'create_campaign')  return `✅ Campanha criada: **${a.input.name}**`;
            if (a.tool === 'update_agent')     return `✅ Agente atualizado`;
            return null;
          })
          .filter(Boolean);
        if (actionSummary.length > 0) content = actionSummary.join('\n') + '\n\n' + answer;
      }
      setHistory(h => [...h, { role: 'assistant', content }]);
    } catch (e) {
      setHistory(h => [...h, { role: 'assistant', content: t('helpChat.error') }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const clearHistory = () => {
    setHistory([{ role: 'assistant', content: t('helpChat.greeting') }]);
  };

  const isCallCenter = location.pathname.startsWith('/callcenter');

  return (
    <>
      {/* Botão flutuante — oculto no call center para não sobrepor o botão de envio */}
      {!isCallCenter && (
        <FloatBtn onClick={() => setOpen(v => !v)} title={t('helpChat.helpTitle')} $open={open}>
          {open ? <X size={22} /> : <HelpCircle size={22} />}
        </FloatBtn>
      )}

      {/* Modal de chat */}
      {open && !isCallCenter && (
        <ChatWindow>
          <ChatHeader>
            <HeaderLeft>
              <BotAvatar><Bot size={16} /></BotAvatar>
              <div>
                <HeaderTitle>{t('helpChat.title')}</HeaderTitle>
                <HeaderSub>📍 {pageContext.title}</HeaderSub>
              </div>
            </HeaderLeft>
            <HeaderActions>
              <ClearBtn onClick={clearHistory} title={t('helpChat.clearTitle')}>↺</ClearBtn>
              <CloseBtn onClick={() => setOpen(false)}><X size={16} /></CloseBtn>
            </HeaderActions>
          </ChatHeader>

          <MessageList>
            {history.map((msg, i) => (
              <MessageRow key={i} $role={msg.role}>
                {msg.role === 'assistant' && (
                  <MsgAvatar $role="assistant"><Bot size={13} /></MsgAvatar>
                )}
                <Bubble $role={msg.role}>
                  <MsgText>{msg.content}</MsgText>
                </Bubble>
                {msg.role === 'user' && (
                  <MsgAvatar $role="user"><User size={13} /></MsgAvatar>
                )}
              </MessageRow>
            ))}
            {loading && (
              <MessageRow $role="assistant">
                <MsgAvatar $role="assistant"><Bot size={13} /></MsgAvatar>
                <Bubble $role="assistant">
                  <TypingDots>
                    <span /><span /><span />
                  </TypingDots>
                </Bubble>
              </MessageRow>
            )}
            <div ref={bottomRef} />
          </MessageList>

          <InputArea>
            <ChatInput
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={t('helpChat.placeholder')}
              rows={1}
              disabled={loading}
            />
            <SendBtn aria-label={t('send', { ns: 'common' })} onClick={send} disabled={!input.trim() || loading}>
              {loading ? <Loader size={16} className="spin" /> : <Send size={16} />}
            </SendBtn>
          </InputArea>
        </ChatWindow>
      )}
    </>
  );
};

// ── Styled ────────────────────────────────────────────────────────────────────

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.85) translateY(12px); }
  to   { opacity: 1; transform: scale(1)    translateY(0); }
`;

const FloatBtn = styled.button`
  position: fixed; bottom: 24px; right: 24px; z-index: 500;
  width: 52px; height: 52px; border-radius: 50%;
  background: ${({ $open }) => $open ? theme.colors.gray700 : theme.colors.primary};
  color: white; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 16px rgb(0 0 0 / 0.2);
  transition: ${theme.transitions.fast};
  &:hover { transform: scale(1.08); }
`;

const ChatWindow = styled.div`
  position: fixed; bottom: 88px; right: 24px; z-index: 499;
  width: 360px; height: 520px;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii['2xl']};
  box-shadow: 0 20px 40px rgb(0 0 0 / 0.15);
  display: flex; flex-direction: column;
  animation: ${popIn} 180ms ease;
  overflow: hidden;
  @media (max-width: 480px) {
    width: calc(100vw - 32px); right: 16px; bottom: 80px;
    height: calc(100vh - 120px);
  }
`;

const ChatHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.primary};
  flex-shrink: 0;
`;
const HeaderLeft = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]};`;
const BotAvatar = styled.div`
  width: 32px; height: 32px; border-radius: 50%;
  background: rgb(255 255 255 / 0.2); color: white;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`;
const HeaderTitle = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: white;`;
const HeaderSub   = styled.div`font-size: ${theme.typography.xs}; color: rgb(255 255 255 / 0.75);`;
const HeaderActions = styled.div`display: flex; align-items: center; gap: ${theme.spacing[1]};`;
const ClearBtn = styled.button`
  background: rgb(255 255 255 / 0.15); border: none; color: white;
  width: 28px; height: 28px; border-radius: ${theme.radii.md};
  cursor: pointer; font-size: 16px; display: flex; align-items: center; justify-content: center;
  &:hover { background: rgb(255 255 255 / 0.25); }
`;
const CloseBtn = styled(ClearBtn)``;

const MessageList = styled.div`
  flex: 1; overflow-y: auto; padding: ${theme.spacing[4]};
  display: flex; flex-direction: column; gap: ${theme.spacing[3]};
  background: ${theme.colors.gray50};
`;

const MessageRow = styled.div`
  display: flex; align-items: flex-end; gap: ${theme.spacing[2]};
  flex-direction: ${({ $role }) => $role === 'user' ? 'row-reverse' : 'row'};
`;

const MsgAvatar = styled.div`
  width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: ${({ $role }) => $role === 'assistant' ? theme.colors.primaryLight : theme.colors.secondaryLight};
  color: ${({ $role }) => $role === 'assistant' ? theme.colors.primary : theme.colors.secondary};
`;

const Bubble = styled.div`
  max-width: 78%; padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.radii.xl};
  border-bottom-${({ $role }) => $role === 'user' ? 'right' : 'left'}-radius: 4px;
  background: ${({ $role }) => $role === 'user' ? theme.colors.primary : theme.colors.white};
  color: ${({ $role }) => $role === 'user' ? 'white' : theme.colors.textPrimary};
  box-shadow: ${theme.shadows.sm};
  border: ${({ $role }) => $role === 'assistant' ? `1px solid ${theme.colors.border}` : 'none'};
`;

const MsgText = styled.div`
  font-size: ${theme.typography.sm}; line-height: 1.55;
  white-space: pre-wrap; word-break: break-word;
`;

const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); }
  40%           { transform: translateY(-6px); }
`;
const TypingDots = styled.div`
  display: flex; gap: 4px; padding: 2px 0;
  span {
    width: 7px; height: 7px; border-radius: 50%;
    background: ${theme.colors.textMuted};
    animation: ${bounce} 1.2s infinite;
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;

const InputArea = styled.div`
  display: flex; align-items: flex-end; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]};
  border-top: 1px solid ${theme.colors.border};
  background: ${theme.colors.white};
  flex-shrink: 0;
`;

const ChatInput = styled.textarea`
  flex: 1; resize: none; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg}; padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-family: ${theme.typography.fontFamily}; font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary}; outline: none; max-height: 100px;
  line-height: 1.5;
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
  &:disabled { background: ${theme.colors.gray100}; }
`;

const spinAnim = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const SendBtn = styled.button`
  width: 36px; height: 36px; border-radius: ${theme.radii.md}; flex-shrink: 0;
  background: ${({ disabled }) => disabled ? theme.colors.gray200 : theme.colors.primary};
  color: ${({ disabled }) => disabled ? theme.colors.textMuted : 'white'};
  border: none; cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  display: flex; align-items: center; justify-content: center;
  transition: ${theme.transitions.fast};
  &:hover:not(:disabled) { background: ${theme.colors.primaryHover}; }
  .spin { animation: ${spinAnim} 0.8s linear infinite; }
`;

export default HelpChat;
